import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { WishlistItem } from '../../contexts/WishlistContext';
import { formatPrice } from '../../utils/currency';
import { useAuth } from '../../contexts/AuthContext';

interface WishlistCardProps {
  item: WishlistItem;
  onEdit?: (item: WishlistItem) => void;
  onDelete?: (itemId: number) => void;
  onMoveToCollection?: (itemId: number) => void;
  onCopyDetails?: (item: WishlistItem) => void;
}

const WishlistCard: React.FC<WishlistCardProps> = ({
  item,
  onEdit,
  onDelete,
  onMoveToCollection,
  onCopyDetails
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const { user } = useAuth();

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (user?.username === 'Demo') return;
    onEdit && onEdit(item);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (user?.username === 'Demo') return;
    if (window.confirm(`Are you sure you want to delete "${item.name}" by ${item.house}?`)) {
      onDelete && onDelete(item.id);
    }
  };

  const handleMoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (user?.username === 'Demo') return;
    if (window.confirm(`Move "${item.name}" by ${item.house} to your collection?`)) {
      onMoveToCollection && onMoveToCollection(item.id);
    }
  };

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCopyDetails && onCopyDetails(item);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400';
      case 'medium': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'low': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getSizeDisplay = () => {
    const sizes = [];
    if (item.size_oz) sizes.push(`${item.size_oz} oz`);
    if (item.size_ml) sizes.push(`${item.size_ml} ml`);
    return sizes.join(' / ');
  };

  return (
    <motion.div
      className="card-flip w-full h-[28rem] cursor-pointer"
      onClick={handleFlip}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
      style={{ willChange: "transform" }}
    >
      <div
        className={`card-flip-inner ${isFlipped ? 'transform rotateY-180' : ''}`}
        style={{ transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
      >
        {/* Front Side */}
        <div className="card-flip-front bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {item.name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                  {item.house}
                </p>
              </div>
              <div className="flex items-center space-x-2 ml-2">
                {Boolean(item.is_gift_idea) && (
                  <span className="px-2 py-1 text-xs bg-pink-100 dark:bg-pink-900 text-pink-800 dark:text-pink-200 rounded-full">
                    🎁 Gift Idea
                  </span>
                )}
                <span className={`px-2 py-1 text-xs rounded-full font-medium ${getPriorityColor(item.priority)}`}>
                  {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 space-y-3">
              {item.concentration && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Concentration
                  </span>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {item.concentration ? {
                      'EdC': 'Eau de Cologne',
                      'EdT': 'Eau de Toilette', 
                      'EdP': 'Eau de Parfum',
                      'Parfum': 'Parfum',
                      'Elixir': 'Elixir',
                      'Extrait': 'Extrait de Parfum'
                    }[item.concentration] || item.concentration : ''}
                  </p>
                </div>
              )}

              {getSizeDisplay() && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Size
                  </span>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {getSizeDisplay()}
                  </p>
                </div>
              )}

              {item.price && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Price
                  </span>
                  <p className="text-lg font-bold text-green-600 dark:text-green-400">
                    {formatPrice(item.price, item.currency)}
                  </p>
                </div>
              )}

              {item.retailer && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Retailer
                  </span>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {item.retailer}
                  </p>
                </div>
              )}

              {item.notes_top && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Top Notes
                  </span>
                  <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-normal break-words line-clamp-2">
                    {item.notes_top}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Click to flip for details
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleMoveClick}
                  className="text-gray-400 hover:text-green-500 transition-colors duration-200"
                  title="Move to Collection"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
                <button
                  onClick={handleEditClick}
                  className="text-gray-400 hover:text-blue-500 transition-colors duration-200"
                  title="Edit"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={handleCopyClick}
                  className="text-gray-400 hover:text-purple-500 transition-colors duration-200"
                  title="Copy Details"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  onClick={handleDeleteClick}
                  className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                  title="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Back Side */}
        <div className="card-flip-back bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {item.name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                  {item.house}
                </p>
              </div>
            </div>

            {/* Details */}
            <div className="flex-1 space-y-4 overflow-y-auto custom-scrollbar pr-3">
              {/* Price and Purchase Info */}
              {(item.price || item.retailer) && (
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
                  {item.price && (
                    <div className="text-center mb-3">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide block mb-1">
                        Price
                      </span>
                      <p className="text-xl font-bold text-green-600 dark:text-green-400">
                        {formatPrice(item.price, item.currency)}
                      </p>
                    </div>
                  )}

                  {item.retailer && (
                    <div className="text-center">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide block mb-2">
                        Available at
                      </span>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                        {item.retailer}
                      </p>
                      {item.retailer_link && (
                        <a 
                          href={item.retailer_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Visit Store
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              {(item.notes_top || item.notes_middle || item.notes_base) && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Fragrance Notes
                  </h4>
                  <div className="space-y-1 text-xs">
                    {item.notes_top && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Top:</span>
                        <span className="ml-1 text-gray-600 dark:text-gray-400">{item.notes_top}</span>
                      </div>
                    )}
                    {item.notes_middle && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Middle:</span>
                        <span className="ml-1 text-gray-600 dark:text-gray-400">{item.notes_middle}</span>
                      </div>
                    )}
                    {item.notes_base && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">Base:</span>
                        <span className="ml-1 text-gray-600 dark:text-gray-400">{item.notes_base}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Personal Notes */}
              {item.notes && item.notes.trim() && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    My Notes
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {item.notes}
                  </p>
                </div>
              )}

              {/* Priority and Gift Status */}
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Priority:</span>
                  <span className={`ml-1 px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
                    {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                  </span>
                </div>
                {Boolean(item.is_gift_idea) && (
                  <span className="text-pink-600 dark:text-pink-400 text-xs">
                    🎁 Gift Idea
                  </span>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-center pt-4 border-t border-gray-200 dark:border-gray-700">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Click to flip back
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default WishlistCard;
